import i18n, { type Resource } from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { DEFAULT_LANGUAGE, LOCALE_STORAGE_KEY } from '@/i18n/types'

import plCommonRaw from '@/i18n/locales/pl/common.json?raw'
import plLayoutRaw from '@/i18n/locales/pl/layout.json?raw'
import plAuthRaw from '@/i18n/locales/pl/auth.json?raw'
import plProjectsRaw from '@/i18n/locales/pl/projects.json?raw'
import plRecordingRaw from '@/i18n/locales/pl/recording.json?raw'
import plAnalysisRaw from '@/i18n/locales/pl/analysis.json?raw'
import plQuestionnairesRaw from '@/i18n/locales/pl/questionnaires.json?raw'
import plErrorsRaw from '@/i18n/locales/pl/errors.json?raw'
import plParticipantRaw from '@/i18n/locales/pl/participant.json?raw'
import plDashboardRaw from '@/i18n/locales/pl/dashboard.json?raw'
import plMonitoringRaw from '@/i18n/locales/pl/monitoring.json?raw'
import plResultsRaw from '@/i18n/locales/pl/results.json?raw'
import plLiveDemoRaw from '@/i18n/locales/pl/liveDemo.json?raw'
import plQuestionsRaw from '@/i18n/locales/pl/questions.json?raw'
import plEmotionsRaw from '@/i18n/locales/pl/emotions.json?raw'
import plRespondentsRaw from '@/i18n/locales/pl/respondents.json?raw'
import plTeamRaw from '@/i18n/locales/pl/team.json?raw'
import plPlansRaw from '@/i18n/locales/pl/plans.json?raw'

import enCommonRaw from '@/i18n/locales/en/common.json?raw'
import enLayoutRaw from '@/i18n/locales/en/layout.json?raw'
import enAuthRaw from '@/i18n/locales/en/auth.json?raw'
import enProjectsRaw from '@/i18n/locales/en/projects.json?raw'
import enRecordingRaw from '@/i18n/locales/en/recording.json?raw'
import enAnalysisRaw from '@/i18n/locales/en/analysis.json?raw'
import enQuestionnairesRaw from '@/i18n/locales/en/questionnaires.json?raw'
import enErrorsRaw from '@/i18n/locales/en/errors.json?raw'
import enParticipantRaw from '@/i18n/locales/en/participant.json?raw'
import enDashboardRaw from '@/i18n/locales/en/dashboard.json?raw'
import enMonitoringRaw from '@/i18n/locales/en/monitoring.json?raw'
import enResultsRaw from '@/i18n/locales/en/results.json?raw'
import enLiveDemoRaw from '@/i18n/locales/en/liveDemo.json?raw'
import enQuestionsRaw from '@/i18n/locales/en/questions.json?raw'
import enEmotionsRaw from '@/i18n/locales/en/emotions.json?raw'
import enRespondentsRaw from '@/i18n/locales/en/respondents.json?raw'
import enTeamRaw from '@/i18n/locales/en/team.json?raw'
import enPlansRaw from '@/i18n/locales/en/plans.json?raw'

function parseLocale(raw: string): Record<string, unknown> {
  return JSON.parse(raw) as Record<string, unknown>
}

const plCommon = parseLocale(plCommonRaw)
const plLayout = parseLocale(plLayoutRaw)
const plAuth = parseLocale(plAuthRaw)
const plProjects = parseLocale(plProjectsRaw)
const plRecording = parseLocale(plRecordingRaw)
const plAnalysis = parseLocale(plAnalysisRaw)
const plQuestionnaires = parseLocale(plQuestionnairesRaw)
const plErrors = parseLocale(plErrorsRaw)
const plParticipant = parseLocale(plParticipantRaw)
const plDashboard = parseLocale(plDashboardRaw)
const plMonitoring = parseLocale(plMonitoringRaw)
const plResults = parseLocale(plResultsRaw)
const plLiveDemo = parseLocale(plLiveDemoRaw)
const plQuestions = parseLocale(plQuestionsRaw)
const plEmotions = parseLocale(plEmotionsRaw)
const plRespondents = parseLocale(plRespondentsRaw)
const plTeam = parseLocale(plTeamRaw)
const plPlans = parseLocale(plPlansRaw)

const enCommon = parseLocale(enCommonRaw)
const enLayout = parseLocale(enLayoutRaw)
const enAuth = parseLocale(enAuthRaw)
const enProjects = parseLocale(enProjectsRaw)
const enRecording = parseLocale(enRecordingRaw)
const enAnalysis = parseLocale(enAnalysisRaw)
const enQuestionnaires = parseLocale(enQuestionnairesRaw)
const enErrors = parseLocale(enErrorsRaw)
const enParticipant = parseLocale(enParticipantRaw)
const enDashboard = parseLocale(enDashboardRaw)
const enMonitoring = parseLocale(enMonitoringRaw)
const enResults = parseLocale(enResultsRaw)
const enLiveDemo = parseLocale(enLiveDemoRaw)
const enQuestions = parseLocale(enQuestionsRaw)
const enEmotions = parseLocale(enEmotionsRaw)
const enRespondents = parseLocale(enRespondentsRaw)
const enTeam = parseLocale(enTeamRaw)
const enPlans = parseLocale(enPlansRaw)

const resources: Resource = {
  pl: {
    common: plCommon,
    layout: plLayout,
    auth: plAuth,
    projects: plProjects,
    recording: plRecording,
    analysis: plAnalysis,
    questionnaires: plQuestionnaires,
    errors: plErrors,
    participant: plParticipant,
    dashboard: plDashboard,
    monitoring: plMonitoring,
    results: plResults,
    liveDemo: plLiveDemo,
    questions: plQuestions,
    emotions: plEmotions,
    respondents: plRespondents,
    team: plTeam,
    plans: plPlans,
  },
  en: {
    common: enCommon,
    layout: enLayout,
    auth: enAuth,
    projects: enProjects,
    recording: enRecording,
    analysis: enAnalysis,
    questionnaires: enQuestionnaires,
    errors: enErrors,
    participant: enParticipant,
    dashboard: enDashboard,
    monitoring: enMonitoring,
    results: enResults,
    liveDemo: enLiveDemo,
    questions: enQuestions,
    emotions: enEmotions,
    respondents: enRespondents,
    team: enTeam,
    plans: enPlans,
  },
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    ns: ['common', 'layout', 'auth', 'projects', 'recording', 'analysis', 'questionnaires', 'errors', 'participant', 'dashboard', 'monitoring', 'results', 'liveDemo', 'questions', 'respondents', 'team', 'plans'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  })

export { isAppLanguage } from '@/i18n/types'
export default i18n
